import * as React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

type ReleaseNotes =
  | string
  | null
  | Array<{
      version?: string;
      note?: string | null;
    }>;

type AutoUpdateDetails = {
  version?: string;
  releaseName?: string | null;
  releaseNotes?: ReleaseNotes;
};

type AutoUpdateStatus = {
  pending: boolean;
  promptPending: boolean;
  version?: string;
  releaseName?: string | null;
  releaseNotes?: ReleaseNotes;
  deferred: boolean;
  deferredVersion: string | null;
};

function normalizeReleaseNotes(notes?: ReleaseNotes) {
  if (!notes) return [];
  if (typeof notes === 'string') {
    return notes.trim().length > 0 ? [notes.trim()] : [];
  }
  if (!Array.isArray(notes)) return [];
  return notes
    .map(entry => (typeof entry?.note === 'string' ? entry.note.trim() : ''))
    .filter(note => note.length > 0);
}

const AutoUpdatePrompt: React.FC = () => {
  const autoUpdateApi = window.electronAPI?.autoUpdate;
  const [details, setDetails] = React.useState<AutoUpdateDetails | null>(null);
  const [open, setOpen] = React.useState(false);
  const [installing, setInstalling] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const effectiveVersion = React.useMemo(() => {
    return details?.version || details?.releaseName || undefined;
  }, [details?.releaseName, details?.version]);

  const releaseNotes = React.useMemo(() => {
    return normalizeReleaseNotes(details?.releaseNotes);
  }, [details?.releaseNotes]);

  const showPrompt = React.useCallback(
    (nextDetails: AutoUpdateDetails | null) => {
      if (!nextDetails) return;
      setError(null);
      setInstalling(false);
      setDetails(prev => {
        if (!prev) return nextDetails;
        const sameVersion =
          (prev.version || prev.releaseName || '') ===
          (nextDetails.version || nextDetails.releaseName || '');
        if (!sameVersion) {
          return nextDetails;
        }
        // Merge notes if we already display the same version.
        return {
          ...prev,
          releaseNotes:
            nextDetails.releaseNotes !== undefined
              ? nextDetails.releaseNotes
              : prev.releaseNotes,
        };
      });
      setOpen(true);
    },
    [],
  );

  React.useEffect(() => {
    if (!autoUpdateApi) return undefined;

    let mounted = true;

    const handleReady = (payload: AutoUpdateDetails) => {
      if (!mounted) return;
      showPrompt(payload);
    };

    const unsubscribe = autoUpdateApi.onUpdateReady?.(handleReady);

    autoUpdateApi
      .getStatus?.()
      .then((status: AutoUpdateStatus | undefined) => {
        if (!mounted || !status) return;
        if (status.pending && status.promptPending) {
          handleReady({
            version: status.version,
            releaseName: status.releaseName,
            releaseNotes: status.releaseNotes,
          });
        }
      })
      .catch(() => {
        /* ignore */
      });

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [autoUpdateApi, showPrompt]);

  const handleCloseAttempt = React.useCallback(
    (_event: unknown, reason: string) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return;
      }
      setOpen(false);
    },
    [],
  );

  const handleInstallNow = React.useCallback(async () => {
    if (!autoUpdateApi?.installNow) return;
    setError(null);
    setInstalling(true);
    try {
      const result = await autoUpdateApi.installNow();
      if (!result?.ok) {
        const message =
          result?.error === 'no-update'
            ? "Aucune mise à jour n'est prête à être installée."
            : 'Le lancement de la mise à jour a échoué.';
        setInstalling(false);
        setError(message);
        toast.error(message);
      }
    } catch (err) {
      setInstalling(false);
      const message =
        err instanceof Error ? err.message : 'Le lancement de la mise à jour a échoué.';
      setError(message);
      toast.error(message);
    }
  }, [autoUpdateApi]);

  const handleRemindLater = React.useCallback(async () => {
    if (!autoUpdateApi?.remindLater) return;
    setError(null);
    try {
      const result = await autoUpdateApi.remindLater();
      if (!result?.ok) {
        const message =
          result?.error === 'no-update'
            ? "Aucune mise à jour n'est prête à être installée."
            : 'Impossible de reporter la mise à jour.';
        setError(message);
        toast.error(message);
        return;
      }
      setOpen(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Impossible de reporter la mise à jour.';
      setError(message);
      toast.error(message);
    }
  }, [autoUpdateApi]);

  if (!autoUpdateApi) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseAttempt}
      disableEscapeKeyDown
      aria-labelledby="auto-update-dialog-title"
    >
      <DialogTitle id="auto-update-dialog-title">Mise à jour disponible</DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Typography gutterBottom>
          Une nouvelle version d&apos;Expedinet est prête à être installée.
          {effectiveVersion ? ` (version ${effectiveVersion})` : null}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          L&apos;application redémarrera après l&apos;installation. Assurez-vous de sauvegarder
          votre travail avant de continuer.
        </Typography>

        {releaseNotes.length > 0 ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Notes de version
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              {releaseNotes.map((note, index) => (
                <li key={`${effectiveVersion ?? 'update'}-${index}`}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {note}
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleRemindLater} disabled={installing} color="inherit">
          Au prochain démarrage
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleInstallNow}
          disabled={installing}
          startIcon={installing ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Installer maintenant
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoUpdatePrompt;
